import { useTranslation } from 'react-i18next';
import { Card } from '../ui/Card';
import type { TrendSignal } from '../../types/api.js';

interface TrendSignalCardProps {
  trendSignal: TrendSignal;
}

/** @graph-connects none */
const DIRECTION_CONFIG: Record<
  TrendSignal['direction'],
  { icon: string; label: string; color: string }
> = {
  up: { icon: '🔥', label: 'trend.up', color: 'var(--color-success)' },
  slightly_up: { icon: '📈', label: 'trend.slightlyUp', color: 'var(--color-success)' },
  stable: { icon: '➡️', label: 'trend.stable', color: 'var(--color-text-secondary)' },
  slightly_down: { icon: '📉', label: 'trend.slightlyDown', color: 'var(--color-warning)' },
  down: { icon: '⬇️', label: 'trend.down', color: 'var(--color-error)' },
  unknown: { icon: '❓', label: 'trend.unknown', color: 'var(--color-text-muted)' },
};

/**
 * @graph-stack mall
 * @graph-domain Operations
 * @graph-business トレンドシグナル表示。需要方向性・バズスコア・上昇/下降シグナル・市場規模を可視化
 * @graph-connects none
 */
export function TrendSignalCard({ trendSignal }: TrendSignalCardProps) {
  const { t } = useTranslation();
  const config = DIRECTION_CONFIG[trendSignal.direction];

  return (
    <Card variant="glass">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">
        {t('market.trendTitle')}
      </h3>

      {/* トレンド方向 */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-2xl">{config.icon}</span>
        <div>
          <span className="text-lg font-bold" style={{ color: config.color }}>
            {t(config.label)}
          </span>
          {trendSignal.buzzScore > 0 && (
            <span className="ml-2 text-xs text-[var(--color-text-muted)]">
              {t('market.buzzScore')}: {trendSignal.buzzScore}/100
            </span>
          )}
        </div>
      </div>

      {/* シグナルキーワード */}
      {trendSignal.signals.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {trendSignal.signals.map((s, i) => (
            <span
              key={i}
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: s.type === 'up' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: s.type === 'up' ? 'var(--color-success)' : 'var(--color-error)',
              }}
            >
              {s.type === 'up' ? '↑' : '↓'} {s.keyword}
            </span>
          ))}
        </div>
      )}

      {/* 市場規模 */}
      {trendSignal.marketSizeOku != null && (
        <div className="text-xs text-[var(--color-text-muted)]">
          {t('market.marketSize')}: {trendSignal.marketSizeOku.toLocaleString()}
          {t('market.oku')}
        </div>
      )}
    </Card>
  );
}
